import { Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { Subject } from 'rxjs';

export type NovaSonicStatus = 'disconnected' | 'connecting' | 'connected';
export type NovaSonicMode = 'listening' | 'speaking';

const VOICE_ID = 'amy';
const OUTPUT_SAMPLE_RATE = 24000;
const INPUT_SAMPLE_RATE = 16000;
// 512 samples @ 16 kHz = 32 ms per chunk.
// The previous value of 4096 (256 ms) was the dominant barge-in latency source
// on the client side: the user had to wait a full 256 ms before the first audio
// byte even left the browser. 512 is the lowest value most browsers allow
// without introducing instability; 1024 (64 ms) is a safe fallback if glitches
// appear on lower-end devices.
const SCRIPT_PROCESSOR_BUFFER = 512;

// Sent in place of real mic audio while the user has muted the mic. Bedrock's
// 55s idle timer ignores all-zero PCM (confirmed separately), so a muted mic
// otherwise starves the session of any qualifying input and Bedrock kills the
// stream. This piggybacks on the capture pipeline's proven-reliable 32ms send
// cadence instead of depending on the backend's own out-of-band keepalive
// generator, which has proven unreliable under HTTP/2 flow control. Quiet
// 200Hz tone, matches SCRIPT_PROCESSOR_BUFFER samples so it drops in wherever
// a real chunk would go.
const MUTE_KEEPALIVE_FRAME_B64 = (() => {
  const buf = new Int16Array(SCRIPT_PROCESSOR_BUFFER);
  for (let i = 0; i < SCRIPT_PROCESSOR_BUFFER; i++) {
    buf[i] = Math.round(100 * Math.sin((2 * Math.PI * 200 * i) / INPUT_SAMPLE_RATE));
  }
  const bytes = new Uint8Array(buf.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
})();

/**
 * NovaSonicVoiceService
 *
 * Handles the full WebSocket voice protocol against the NovaSonicWebSocketConstruct backend:
 *   connect → initializeConnection → promptStart → systemPrompt → audioStart
 *   → stream PCM16 audio chunks (audioInput) ← play LPCM16 audio output from Bedrock Nova Sonic
 *
 */
@Injectable({ providedIn: 'root' })
export class NovaSonicVoiceService implements OnDestroy {
  readonly status = signal<NovaSonicStatus>('disconnected');
  readonly mode = signal<NovaSonicMode>('listening');
  /** True while the user's mic volume is above the speech threshold. */
  readonly userSpeaking = signal(false);
  /** True while the user has manually muted their microphone. */
  readonly micMuted = signal(false);

  /**
   * Raw RMS amplitude of the microphone input, updated every ~32 ms from
   * the ScriptProcessorNode's onaudioprocess callback.
   * Intentionally a plain mutable property (not a signal) so the
   * VoiceCapsule canvas loop can read it on every requestAnimationFrame
   * without triggering Angular change detection.
   * Typical range: 0 (silence) – 0.12 (loud speech); values above 0.01
   * indicate audible speech.
   */
  inputLevel = 0;
  readonly transcriptData = signal<any>(null);
  readonly sessionStartedAt = signal<number | null>(null);

  /** Emits each text fragment spoken by the assistant, paired with the turn's messageId. */
  readonly textOutput$ = new Subject<{ text: string; messageId: string }>();
  /** Emits the user's spoken words transcribed by Nova Sonic, paired with a messageId. */
  readonly userText$ = new Subject<{ text: string; messageId: string }>();
  /** Emits error messages. */
  readonly error$ = new Subject<string>();
  /** Emits the verify-transcript result pushed back from the Lambda. */
  readonly verifyResult$ = new Subject<unknown>();
  /** Emits base64 PCM16 audio keyed by messageId once it is ready for a turn. */
  readonly audioReady$ = new Subject<{ messageId: string; audio: string }>();
  // emits when the model times out so the component can show a dialog
  readonly modelTimeout$ = new Subject<void>();

  // The sole authoritative V2V socket. Every asynchronous socket/capture
  // callback also carries a generation so an older session can never affect a
  // replacement session.
  private ws: WebSocket | null = null;
  private sessionGeneration = 0;
  private audioReady = false;
  private onConnectionInitialized: (() => void) | null = null;
  private onPromptReady: (() => void) | null = null;
  private onStreamComplete: (() => void) | null = null;

  private currentContentIsAudio = false;

  // Stable reference so we can deregister the same function in cleanup().
  // Arrow function ensures `this` is bound to the service instance.
  private readonly beforeUnloadHandler = (): void => {
    // Best-effort synchronous shutdown. Stop the processor first so it cannot
    // race endAudio with one final stale audioInput frame.
    this.stopCapture();
    this.audioReady = false;
    this.send({ action: 'endAudio' });
  };

  // When the user tabs back in, Chrome may have suspended the capture AudioContext.
  // Resume it immediately so onaudioprocess fires again and audio keeps flowing.
  private readonly visibilityHandler = (): void => {
    if (document.visibilityState === 'visible') {
      void this.captureCtx?.resume();
    }
  };
  private captureCtx: AudioContext | null = null;
  private playbackCtx: AudioContext | null = null;
  /**
   * AnalyserNode inserted into the playback chain so that getOutputLevel()
   * can compute the RMS amplitude of the assistant's audio in real time.
   * Created alongside playbackCtx in playAudio(); torn down in cleanup().
   */
  private playbackAnalyser: AnalyserNode | null = null;
  private playbackAnalyserData: Float32Array<ArrayBuffer> | null = null;
  /**
   * AnalyserNode tapped off the microphone source so the VoiceCapsule can read
   * the user's live waveform (getByteTimeDomainData) and spectrum
   * (getByteFrequencyData) for phase-accurate ripple animation. It is a passive
   * tap — never connected to a destination — so it does not affect the captured
   * audio that is streamed to Bedrock. Created in startCapture(); torn down in
   * cleanup().
   */
  private captureAnalyser: AnalyserNode | null = null;
  private micStream: MediaStream | null = null;
  // ScriptProcessorNode is deprecated but requires no external worklet asset files.
  private scriptProcessor: ScriptProcessorNode | null = null;
  private playbackQueue: AudioBuffer[] = [];
  // Handles to AudioBufferSourceNodes that have been scheduled (some starting
  // in the future). Tracked so a barge-in can stop them immediately instead of
  // letting already-queued assistant audio play out — playback inertia that
  // otherwise feels like lag.
  private scheduledSources: AudioBufferSourceNode[] = [];
  private nextPlayAt = 0;
  // Set when contentEnd arrives while audio is still scheduled/playing, so the
  // "speaking" pulse keeps going until the last chunk actually finishes rather
  // than cutting out the moment the server says it's done sending audio.
  private pendingSpeechEnd = false;
  private recentAssistantTexts = new Set<string>();

  // Per-turn buffers for verify-transcript
  private currentTurnAudioChunks: string[] = [];
  private currentTurnText = '';
  private currentTurnMessageId = '';
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SPEECH_THRESHOLD = 0.01; // RMS level above which we consider user speaking
  private readonly SILENCE_DELAY_MS = 800; // ms of silence before hiding Transcribing...

  // Set from connectionInitialized event
  private connectionId: string | null = null;
  private verifyEndpoint: string | null = null;
  private messageIdsSent = new Set<string>();

  // Per-session monotonic sequence number attached to every audioInput chunk
  // so the backend can detect/reorder chunks that arrive at SQS out of order
  // (e.g. concurrent router Lambda invocations racing each other). Reset to 0
  // at the start of each session in audioStart and incremented by exactly 1
  // per chunk sent during that session.
  private audioSequence = 0;

  // Diagnostics for the 55s Bedrock idle-timeout investigation: onaudioprocess
  // fires every 32ms which is far too noisy to log unconditionally, so this
  // throttles to once per second and captures the state that would explain a
  // gap — did the callback stop firing, or did it fire into a closed/suspended
  // pipe? Remove once the root cause of the idle-timeout is confirmed.
  private lastAudioProcessLogAt = 0;

  constructor(private readonly ngZone: NgZone) {}

  /** The API Gateway WebSocket connection ID for the current/last voice session, used to link a voice-to-text fallback back to its voice transcript. Persists across cleanup() (survives modelTimeout/disconnect) and is only reset when a new session starts via start(). */
  getConnectionId(): string | null {
    return this.connectionId;
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  async start(wsUrl: string, sessionData: { productType: string; brand: string; planNumber: string }): Promise<void> {
    if (this.status() === 'connecting' || this.status() === 'connected') {
      return;
    }

    // Invalidate every outstanding callback before beginning a new session.
    // This also disposes any capture left behind by a failed/partially closed
    // connection before a replacement WebSocket can be created.
    this.cleanup();
    const generation = ++this.sessionGeneration;
    this.audioReady = false;
    this.status.set('connecting');
    this.recentAssistantTexts.clear();
    // New session starting — drop the previous session's ID now that any
    // fallback linking to it has had its chance (cleanup() no longer clears this).
    this.connectionId = null;

    try {
      // Request permission before opening the socket, then release this probe.
      // Capture is opened only after the backend's audioReady acknowledgement.
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permissionStream.getTracks().forEach((track) => track.stop());
      if (generation !== this.sessionGeneration) return;
    } catch (err) {
      if (generation === this.sessionGeneration) this.cleanup();
      throw err;
    }

    const token = this.getCookie('AWS_IDT');
    const url = `${wsUrl}?Authorization=${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    this.ws = socket;

    // Register unload guards as soon as the session begins so we send
    // endAudio even if the user closes the tab or navigates away.
    // beforeunload: desktop tab-close / navigation.
    // pagehide:     mobile background / back-forward cache dismissal.
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.beforeUnloadHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    socket.onopen = async () => {
      try {
        if (!this.isActiveSession(socket, generation)) return;
        this.status.set('connected');
        this.sessionStartedAt.set(Date.now());
        // Send initializeConnection first and wait for the server ack before
        // proceeding. promptStart/audioStart hit a DynamoDB connection guard;
        // sending them before the initializeConnection write commits causes a
        // 403 → APIGW "Internal server error" race condition.
        this.send(
          {
            action: 'initializeConnection',
            data: {
              productType: sessionData.productType ?? '',
              brand: sessionData.brand ?? '',
              planNumber: sessionData.planNumber ?? '',
            },
          },
          socket
        );
        await this.waitForConnectionInitialized();
        if (!this.isActiveSession(socket, generation)) return;
        // Wait for the router to persist PROMPT_READY before issuing audioStart.
        // WebSocket route Lambda invocations are concurrent, so sending these
        // adjacent frames can otherwise race and leave the connection record at
        // PROMPT_READY after audioStart has already moved it to STREAMING.
        this.send({ action: 'promptStart', data: { voiceId: VOICE_ID, outputSampleRate: OUTPUT_SAMPLE_RATE } }, socket);
        await this.waitForPromptReady();
        if (!this.isActiveSession(socket, generation)) return;
        this.send({ action: 'audioStart' }, socket);
        // Do not start capture here: audioReady is the backend's guarantee
        // that this exact session is ready to accept audioInput frames.
      } catch (error) {
        if (!this.isActiveSession(socket, generation)) return;
        console.error('[nova-sonic] Failed while opening voice session', {
          error,
          readyState: this.ws?.readyState,
          connectionId: this.connectionId,
        });
        this.error$.next('Failed to start voice session');
        this.cleanup(socket);
      }
    };

    socket.onmessage = (ev) => this.handleMessage(ev, socket, generation);

    socket.onerror = () => {
      if (!this.isActiveSession(socket, generation)) return;
      this.error$.next('WebSocket connection failed');
      this.cleanup(socket);
    };

    socket.onclose = () => {
      if (!this.isActiveSession(socket, generation)) return;
      this.cleanup(socket);
    };
  }

  async stop(): Promise<void> {
    // No processor may emit a frame after endAudio has ended the session.
    this.stopCapture();
    this.audioReady = false;
    this.send({ action: 'endAudio' });
    // Wait for the backend to signal stream completion (which means the
    // finally block has started and the transcript has been queued for
    // saving). Fall back to 5 s in case the server never responds.
    await Promise.race([this.waitForStreamComplete(), new Promise<void>((resolve) => setTimeout(resolve, 5_000))]);
    this.cleanup();
  }

  /** Mutes/unmutes the mic at the hardware track level so no audio leaves the device while muted. */
  setMicMuted(muted: boolean): void {
    this.micMuted.set(muted);
    this.micStream?.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  toggleMicMute(): void {
    this.setMicMuted(!this.micMuted());
  }

  async verifyTranscript(): Promise<void> {
    if (!this.currentTurnText || !this.currentTurnMessageId || !this.connectionId) return;
    if (this.messageIdsSent.has(this.currentTurnMessageId)) return;
    this.messageIdsSent.add(this.currentTurnMessageId);

    const payload: Record<string, unknown> = {
      connectionId: this.connectionId,
      messageId: this.currentTurnMessageId,
      text: this.currentTurnText,
      language: 'en-GB',
      sampleRate: 24000,
    };

    if (this.currentTurnAudioChunks.length > 0) {
      payload['audio'] = this.mergeAudioChunks(this.currentTurnAudioChunks);
      this.audioReady$.next({ messageId: this.currentTurnMessageId, audio: payload['audio'] as string });
    }

    const endpoint = this.verifyEndpoint;
    if (endpoint) {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch((err) => console.error('[nova-sonic][verify] HTTP POST failed:', err));
    } else {
      // Fallback: text-only via WebSocket (no audio — within 128 KB limit)
      this.send({
        action: 'verify-transcript',
        data: { messageId: this.currentTurnMessageId, text: this.currentTurnText },
      });
    }

    this.currentTurnAudioChunks = [];
    this.currentTurnText = '';
    this.currentTurnMessageId = '';
  }

  private waitForPromptReady(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.onPromptReady = () => {
        this.onPromptReady = null;
        resolve();
      };
    });
  }

  private waitForStreamComplete(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.onStreamComplete = () => {
        this.onStreamComplete = null;
        resolve();
      };
    });
  }

  // ─── Internal ──────────────────────────────────────────────────────────────

  private waitForConnectionInitialized(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.onConnectionInitialized = () => {
        this.onConnectionInitialized = null;
        resolve();
      };
    });
  }

  private isActiveSession(socket: WebSocket, generation: number): boolean {
    return this.ws === socket && this.sessionGeneration === generation;
  }

  /**
   * Sends a protocol message only when the supplied socket is still this
   * service's authoritative V2V connection. Supplying the session-scoped
   * socket prevents asynchronous callbacks from writing to a replacement
   * connection after a reconnect.
   *
   * @param payload Message to serialise and send over the WebSocket.
   * @param socket Socket expected to own the message; defaults to the active socket.
   */
  private send(payload: unknown, socket = this.ws): void {
    if (socket && socket === this.ws && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  }

  /**
   * Opens the microphone and attaches its processor to one specific V2V
   * session. Capture is permitted only after that session's `audioReady`
   * acknowledgement; if the session was replaced while getUserMedia was
   * pending, the newly acquired stream is stopped without creating a processor.
   *
   * @param socket The WebSocket that received `audioReady`.
   * @param generation Monotonic token for the socket session.
   */
  private async startCapture(socket: WebSocket, generation: number): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    if (!this.isActiveSession(socket, generation) || !this.audioReady) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.micStream = stream;

    // Set sampleRate to 16kHz so no resampling is needed before sending to Bedrock
    this.captureCtx = new AudioContext({ sampleRate: INPUT_SAMPLE_RATE });
    const source = this.captureCtx.createMediaStreamSource(this.micStream);

    // Passive tap for the VoiceCapsule visualiser. A moderate fftSize (512)
    // gives the ripple both a detailed waveform (256 time-domain samples) and
    // enough spectral resolution to map low-to-mid speech energy; a modest
    // smoothing constant keeps the frequency read responsive rather than laggy.
    this.captureAnalyser = this.captureCtx.createAnalyser();
    this.captureAnalyser.fftSize = 512;
    this.captureAnalyser.smoothingTimeConstant = 0.6;
    source.connect(this.captureAnalyser);

    this.scriptProcessor = this.captureCtx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER, 1, 1);

    this.scriptProcessor.onaudioprocess = (e) => {
      // Throttled diagnostic: proves whether onaudioprocess keeps firing
      // (and what state ws/captureCtx are in) during the idle gap that
      // precedes the 55s Bedrock timeout. See lastAudioProcessLogAt above.
      const now = performance.now();
      if (now - this.lastAudioProcessLogAt > 1_000) {
        this.lastAudioProcessLogAt = now;
      }

      // This closure is bound to the socket and generation that created it.
      // Never fall back to this.ws: that is how an old processor can feed a
      // newly-created session (or vice versa) after a reconnect.
      if (!this.isActiveSession(socket, generation) || !this.audioReady || socket.readyState !== WebSocket.OPEN) return;

      if (this.micMuted()) {
        // Real mic samples are already blocked at the hardware level
        // (setMicMuted disables the MediaStreamTrack) — this sends a
        // synthetic tone in their place purely to keep Bedrock's idle
        // timer satisfied. See MUTE_KEEPALIVE_FRAME_B64 above.
        this.send({ action: 'audioInput', data: MUTE_KEEPALIVE_FRAME_B64, sequence: this.audioSequence++ }, socket);
        return;
      }

      const float32 = e.inputBuffer.getChannelData(0);

      // ── VAD: compute RMS and drive userSpeaking signal ──
      let sum = 0;
      for (let i = 0; i < float32.length; i++) sum += float32[i] * float32[i];
      const rms = Math.sqrt(sum / float32.length);
      // Expose the raw RMS so VoiceCapsule can scale the animation to mic volume.
      this.inputLevel = rms;
      if (rms > this.SPEECH_THRESHOLD) {
        // onaudioprocess runs outside Angular's zone (zone.js doesn't patch
        // the Web Audio API), so signal writes here need an explicit
        // ngZone.run() or the "Transcribing..." UI never re-renders.
        if (this.silenceTimer) {
          clearTimeout(this.silenceTimer);
          this.silenceTimer = null;
        }
      } else if (this.userSpeaking() && !this.silenceTimer) {
        this.silenceTimer = setTimeout(() => {
          this.silenceTimer = null;
        }, this.SILENCE_DELAY_MS);
      }

      const pcm16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) {
        pcm16[i] = Math.max(-32768, Math.min(32767, Math.round(float32[i] * 32767)));
      }

      const bytes = new Uint8Array(pcm16.buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      this.send({ action: 'audioInput', data: btoa(binary), sequence: this.audioSequence++ }, socket);
    };

    source.connect(this.scriptProcessor);
    // Must connect to destination for the processor to fire even though we don't want output
    this.scriptProcessor.connect(this.captureCtx.destination);
  }

  private transcriptCompleteSubject = new Subject<any['data']>();
  transcriptComplete$ = this.transcriptCompleteSubject.asObservable();

  private handleMessage(ev: MessageEvent, socket: WebSocket, generation: number): void {
    // Late messages from a closed/replaced socket must not alter the active
    // session's state or start its microphone capture.
    if (!this.isActiveSession(socket, generation)) return;

    let msg: { event: string; data?: any; [key: string]: any };
    try {
      msg = JSON.parse(ev.data as string);
    } catch (error) {
      console.error('[nova-sonic] Could not parse WebSocket message', {
        error,
        rawMessage: ev.data,
        connectionId: this.connectionId,
      });
      return;
    }

    switch (msg.event) {
      case 'connectionInitialized':
        this.connectionId = msg['connectionId'] ?? msg.data?.connectionId ?? null;
        this.onConnectionInitialized?.();
        break;

      case 'promptReady':
        this.onPromptReady?.();
        break;

      case 'audioReady':
        // The backend has now associated this WebSocket with its Bedrock/SQS
        // session. Only this acknowledgement permits microphone capture.
        if (!this.isActiveSession(socket, generation) || this.audioReady) break;
        this.audioReady = true;
        this.audioSequence = 0;
        void this.startCapture(socket, generation).catch((error) => {
          console.error('[nova-sonic] Failed to start microphone capture after audioReady', {
            error,
            connectionId: this.connectionId,
          });
          if (this.isActiveSession(socket, generation)) {
            this.error$.next('Failed to start microphone capture');
            this.cleanup(socket);
          }
        });
        break;

      case 'contentStart':
        if (msg.data?.type === 'AUDIO') {
          this.mode.set('speaking');
          this.currentContentIsAudio = true;
          // A new audio block started before the previous one's tail finished
          // draining — cancel any pending flip-to-listening so it doesn't fire
          // mid-sentence once that old tail's onended callback runs.
          this.pendingSpeechEnd = false;
        } else {
          this.currentContentIsAudio = false;
        }
        break;

      case 'textOutput':
        if (msg.data?.content && msg?.data?.role === 'ASSISTANT') {
          const normalized = (msg.data?.content as string).replace(/\s+/g, ' ').trim();

          if (!normalized) {
            break;
          }

          // filter out interruptions UI-side
          if (normalized.startsWith('{')) {
            try {
              const parsedContent = JSON.parse(normalized);

              if (parsedContent.interrupted) {
                // Nova Sonic reports the previous turn was interrupted — stop
                // any assistant audio still queued so it goes quiet at once.
                this.flushPlayback();
                break;
              }
            } catch {}
          }

          // do not add duplicates
          if (this.recentAssistantTexts.has(normalized)) {
            break;
          }

          this.recentAssistantTexts.add(normalized);
          this.currentTurnMessageId = crypto.randomUUID();
          this.currentTurnText = normalized;
          this.textOutput$.next({ text: normalized, messageId: this.currentTurnMessageId });
        } else {
          if (msg.data?.role === 'USER') {
            this.userSpeaking.set(true);
            this.userText$.next({ text: msg.data.content as string, messageId: crypto.randomUUID() });
            this.userSpeaking.set(false);
          }
        }
        break;

      case 'inputTranscript':
        if (msg.data?.content) {
          // The user started speaking — barge-in. Cut off any assistant audio
          // still scheduled so the conversation feels responsive.
          this.flushPlayback();
          this.recentAssistantTexts.clear();
          const userMessageId = crypto.randomUUID();
          this.userText$.next({ text: msg.data.content as string, messageId: userMessageId });
        }
        break;

      case 'audioOutput':
        if (msg.data?.content) {
          this.currentTurnAudioChunks.push(msg.data.content as string);
          void this.playAudio(msg.data.content as string);
        }
        break;

      case 'contentEnd':
        this.currentContentIsAudio = false;
        this.markSpeechEndOrWaitForPlayback();
        break;

      // inside onmessage switch:
      case 'transcriptComplete':
        this.transcriptData.set(msg.data);
        break;

      case 'streamComplete':
        // Resolve any pending stop() call before tearing down so the
        // caller knows the backend has entered its finally block and
        // the transcript publish is guaranteed.
        this.onStreamComplete?.();
        this.cleanup(socket);
        break;
      // this is the specific event for the model timeout
      // it would otherwise fall under the generic error handler
      case 'modelTimeout':
        // The backend has already timed out this stream. Invalidate capture
        // immediately rather than sending any further frames/endAudio.
        this.cleanup(socket);
        this.modelTimeout$.next();
        break;

      case 'error': {
        // Keep the complete payload as an object in the browser console. In
        // particular, this exposes whether `details` was already converted to
        // "[object Object]" by the backend before it reached the client.
        const message = (msg.data?.message as string | undefined) ?? 'Unknown Nova Sonic error';
        console.error('[nova-sonic] Backend streaming error', {
          message,
          details: msg.data?.details,
          errorData: msg.data,
          fullMessage: msg,
          connectionId: this.connectionId,
          status: this.status(),
          audioSequence: this.audioSequence,
        });
        this.error$.next(message);
        this.cleanup(socket);
        break;
      }
    }
  }

  /**
   * Returns the RMS amplitude (0–1) of the audio currently being played back
   * by the assistant. Called each frame by VoiceCapsule to drive the warm-tone
   * animation during the speaking state.
   * Returns 0 when no audio is playing or the analyser is not yet initialised.
   */
  /**
   * Live microphone AnalyserNode, or null before capture starts. Read by the
   * VoiceCapsule each animation frame to drive the "listening" ripple from the
   * user's actual voice signal (waveform shape + low-mid spectral energy).
   */
  getInputAnalyser(): AnalyserNode | null {
    return this.captureAnalyser;
  }

  /**
   * Live assistant-playback AnalyserNode, or null before any audio has played.
   * Read by the VoiceCapsule each animation frame to drive the "speaking"
   * ripple from Nova Sonic's actual output signal.
   */
  getOutputAnalyser(): AnalyserNode | null {
    return this.playbackAnalyser;
  }

  getOutputLevel(): number {
    if (!this.playbackAnalyser || !this.playbackAnalyserData) return 0;
    this.playbackAnalyser.getFloatTimeDomainData(this.playbackAnalyserData);
    let sum = 0;
    for (const sample of this.playbackAnalyserData) {
      sum += sample * sample;
    }
    return Math.sqrt(sum / this.playbackAnalyserData.length);
  }

  private async playAudio(base64: string): Promise<void> {
    if (!this.playbackCtx) {
      this.playbackCtx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
      // Insert an AnalyserNode between sources and the destination so that
      // getOutputLevel() can meter the assistant's voice amplitude in real time.
      this.playbackAnalyser = this.playbackCtx.createAnalyser();
      // Matches the capture-side analyser (see startCapture) so the VoiceCapsule
      // can read the assistant's waveform + spectrum with the same resolution.
      this.playbackAnalyser.fftSize = 512;
      this.playbackAnalyser.smoothingTimeConstant = 0.6;
      this.playbackAnalyserData = new Float32Array(this.playbackAnalyser.fftSize) as Float32Array<ArrayBuffer>;
      this.playbackAnalyser.connect(this.playbackCtx.destination);
    }

    // Decode base64 LPCM16 → Float32
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768;
    }

    const buffer = this.playbackCtx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);
    this.playbackQueue.push(buffer);
    this.drainPlaybackQueue();
  }

  private drainPlaybackQueue(): void {
    if (!this.playbackCtx) return;
    const ctx = this.playbackCtx;

    while (this.playbackQueue.length > 0) {
      const buf = this.playbackQueue.shift()!;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      // Route audio through the analyser so getOutputLevel() can meter it.
      src.connect(this.playbackAnalyser ?? ctx.destination);
      const startAt = Math.max(ctx.currentTime, this.nextPlayAt);
      src.start(startAt);
      this.nextPlayAt = startAt + buf.duration;

      // Track the node so flushPlayback() can cancel it on barge-in, and
      // self-remove once it finishes so the list does not grow unbounded.
      this.scheduledSources.push(src);
      src.onended = () => {
        const idx = this.scheduledSources.indexOf(src);
        if (idx !== -1) this.scheduledSources.splice(idx, 1);

        // If contentEnd already arrived and this was the last chunk still
        // catching up, only now is the assistant actually done talking.
        if (this.pendingSpeechEnd && this.scheduledSources.length === 0 && this.playbackQueue.length === 0) {
          this.pendingSpeechEnd = false;
          this.mode.set('listening');
          // Resume capture AudioContext in case Chrome auto-suspended it
          // while the tab was backgrounded during the assistant response.
          void this.captureCtx?.resume();
        }
      };
    }
  }

  // contentEnd means the server has finished *sending* audio for this turn,
  // not that playback has caught up — scheduled chunks can still be seconds
  // away from actually finishing. Only flip to "listening" immediately if
  // there's nothing left to play; otherwise wait for the last chunk's onended.
  private markSpeechEndOrWaitForPlayback(): void {
    const ctx = this.playbackCtx;
    const stillPlaying = this.scheduledSources.length > 0 || (ctx != null && this.nextPlayAt > ctx.currentTime);

    if (stillPlaying) {
      this.pendingSpeechEnd = true;
    } else {
      this.mode.set('listening');
      void this.captureCtx?.resume();
    }
  }

  /**
   * Stops and discards all scheduled/playing assistant audio. Called when the
   * user barges in (inputTranscript) or Nova Sonic reports an interruption, so
   * the assistant goes quiet immediately instead of finishing buffered speech.
   */
  private flushPlayback(): void {
    for (const src of this.scheduledSources) {
      try {
        src.onended = null;
        src.stop();
      } catch {
        // Already stopped/ended — safe to ignore.
      }
      try {
        src.disconnect();
      } catch {
        // Already disconnected — safe to ignore.
      }
    }
    this.scheduledSources = [];
    this.playbackQueue = [];
    this.nextPlayAt = 0;
    this.pendingSpeechEnd = false;
    this.mode.set('listening');
  }

  /** Stops all microphone resources before a socket can be ended or replaced. */
  private stopCapture(): void {
    // Clear the callback before disconnecting so an already queued Web Audio
    // task cannot send a frame while the graph is being torn down.
    if (this.scriptProcessor) {
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.captureAnalyser) {
      try {
        this.captureAnalyser.disconnect();
      } catch {
        /* already disconnected */
      }
      this.captureAnalyser = null;
    }

    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    void this.captureCtx?.close();
    this.captureCtx = null;
  }

  private cleanup(expectedSocket?: WebSocket): void {
    // A close/error from an old socket must never dispose its replacement.
    if (expectedSocket && expectedSocket !== this.ws) return;

    ++this.sessionGeneration;
    this.audioReady = false;
    const socketToClose = this.ws;
    this.stopCapture();

    this.playbackCtx?.close();
    this.playbackCtx = null;
    if (this.playbackAnalyser) {
      try {
        this.playbackAnalyser.disconnect();
      } catch {
        /* already disconnected */
      }
      this.playbackAnalyser = null;
    }
    this.playbackAnalyserData = null;
    this.inputLevel = 0;

    this.scheduledSources = [];
    this.playbackQueue = [];
    this.nextPlayAt = 0;
    this.pendingSpeechEnd = false;
    this.recentAssistantTexts.clear();
    this.currentTurnAudioChunks = [];
    this.currentTurnText = '';
    this.currentTurnMessageId = '';
    this.currentContentIsAudio = false;
    // connectionId is deliberately NOT cleared here — modelTimeout (and other
    // disconnect paths) call cleanup() before emitting their event, and a
    // voice-to-text fallback triggered by that event needs to read the just-
    // ended session's connectionId. It's reset in start() instead, once a new
    // session begins.
    this.verifyEndpoint = null;
    this.transcriptData.set(null);
    this.onConnectionInitialized = null;
    this.onPromptReady = null;
    this.onStreamComplete = null;

    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    window.removeEventListener('pagehide', this.beforeUnloadHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);

    // Clear the authoritative reference before closing. Any late event from
    // this socket then fails isActiveSession() and is harmless.
    this.ws = null;
    if (socketToClose && socketToClose.readyState !== WebSocket.CLOSED) {
      socketToClose.close();
    }

    this.status.set('disconnected');
    this.mode.set('listening');
    this.userSpeaking.set(false);
    this.micMuted.set(false);
    this.sessionStartedAt.set(null);
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private getCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() ?? '';
    return '';
  }

  private mergeAudioChunks(chunks: string[]): string {
    const buffers = chunks.map((b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
    const total = buffers.reduce((sum, b) => sum + b.length, 0);
    const merged = new Uint8Array(total);
    let offset = 0;
    for (const b of buffers) {
      merged.set(b, offset);
      offset += b.length;
    }
    let binary = '';
    for (let i = 0; i < merged.length; i++) {
      binary += String.fromCharCode(merged[i]);
    }
    return btoa(binary);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
